<template>
  <div class="component-upload-image">
    <el-upload :action="uploadImgUrl" ref="upload" multiple :limit="limit" :on-exceed="handleExceed" :before-upload="beforeUpload"
     :on-preview="handlePreview" :headers="headers" :on-success="handleUploadSuccess" :http-request="beforeUp" :on-remove="handleRemove" list-type="picture" :file-list="fileList">
      <el-button size="small" type="primary">{{fileTxt}}</el-button>
      <span class="hint" v-if="hintText"><br />{{hintText}}</span>
      
      <!-- 自定义文件列表项 -->
      <template slot="file" slot-scope="{file}">
        <div class="upload-item">
          <img v-if="!isVideoFile(file)" :src="file.url" class="upload-image" @click="handlePreview(file)" />
          <div v-else class="video-preview" @click="handlePreview(file)">
            <i class="el-icon-video-play video-icon"></i>
          </div>
          <i class="el-icon-close delete-icon" @click="handleRemove(file)"></i>
        </div>
      </template>
    </el-upload>
    
    <!-- 图片预览 -->
    <el-image-viewer v-if="showViewer && !isVideoPreview" :on-close="()=>{showViewer=false}" :url-list="previewList" style="z-index: 3000" />
    
    <!-- 视频预览 -->
    <el-dialog v-if="showViewer && isVideoPreview" :visible.sync="showViewer" :append-to-body="true" :modal="true" 
               :close-on-click-modal="true" :close-on-press-escape="true" width="60%" center>
      <div class="video-container">
        <video :src="currentVideoUrl" controls style="width: 100%; height: auto;">
          您的浏览器不支持视频播放
        </video>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { UploadFile } from '@/api/_user';
import { getToken } from '@/utils/auth'
import Sortable from 'sortablejs';
export default {
  components: {
    'el-image-viewer': () => import('element-ui/packages/image/src/image-viewer')
  },
  data() {
    return {
      dialogVisible: false,
      uploadImgUrl: process.env.VUE_APP_BASE_API + '/sys/cosUploader/upload', // 上传的图片服务器地址
      headers: {
        Authorization: 'DU ' + getToken()
      },
      showViewer: false,
      previewList: [],
      isVideoPreview: false,
      currentVideoUrl: '',
      isUploading: false  // 添加上传状态标记

    };
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    fileTxt: {
      type: String,
      default: "上传图片",
    },
    hintText: {
      type: String,
      default: "",
    },
    limit: {
      type: Number,
      default: 10
    }
  },
  computed: {
    // 确保 value 始终是数组。
    // 这里返回的是「渲染用」的副本：给每项补上稳定的 uid 与原始下标 __idx，
    // 因为 el-upload 只按 uid 认元素，而后端取回的历史数据是没有 uid 的
    // （见 handleRemove 的说明）。副本只用于展示和定位，
    // 对外 emit 一律基于原始 value，避免把这两个内部字段写进业务数据。
    fileList() {
      if (!this.value || !Array.isArray(this.value)) {
        return [];
      }
      return this.value.map((item, index) => {
        const obj = (item && typeof item === 'object') ? item : { url: item, name: item };
        return Object.assign({}, obj, {
          uid: (obj.uid === undefined || obj.uid === null) ? `saved-${index}` : obj.uid,
          __idx: index
        });
      });
    }
  },
  created() {
    // 如果 value 不是数组，初始化为空数组
    if (!Array.isArray(this.value)) {
      this.$emit("input", []);
    }
  },
  mounted() {
    this.initDragSort();
  },
  methods: {
    initDragSort() {
      this.$nextTick(() => {
        const el = this.$refs.upload && this.$refs.upload.$el && this.$refs.upload.$el.querySelectorAll('.el-upload-list')[0];
        if (el) {
          Sortable.create(el, {
            onEnd: ({ oldIndex, newIndex }) => {
              // 基于原始 value 调整顺序：fileList 是渲染副本，
              // 对它 splice 既改不动父组件数据，还会把 __idx/uid 带进业务数据
              const arr = (Array.isArray(this.value) ? this.value : []).slice()
              if (arr.length > 0 && oldIndex >= 0 && newIndex >= 0 &&
                  oldIndex < arr.length && newIndex < arr.length) {
                const page = arr[oldIndex]
                arr.splice(oldIndex, 1);
                arr.splice(newIndex, 0, page);
                this.$emit("input", arr);
              }
            }
          });
        }
      });
    },
    // 在原始 value 中定位被点击的这一项。
    // 三级兜底：自定义删除按钮传的是 fileList 的项（带 __idx，最准）；
    // el-upload 内部回调传的是它自己的 file 对象，只能靠 uid、再退到 url 匹配。
    resolveIndex(file) {
      if (!file) return -1;
      if (typeof file.__idx === 'number' && file.__idx >= 0 && file.__idx < this.fileList.length) {
        return file.__idx;
      }
      const list = this.fileList;
      if (file.uid !== undefined && file.uid !== null) {
        const i = list.findIndex(item => item.uid === file.uid);
        if (i > -1) return i;
      }
      if (file.url) {
        // 同一 url 可能重复存在，取第一个匹配即可
        return list.findIndex(item => item.url === file.url);
      }
      return -1;
    },
    handleRemove(file, fileList) {
      // 原实现是 filter(item => item.uid !== file.uid)：
      // 后端取回的历史图片对象上并没有 uid，两边都是 undefined，
      // undefined !== undefined 恒为 false，于是一张都过滤不掉、原样 emit 回去，
      // 表现为「点了删除、界面没了，保存后重新打开图还在」。
      // 改为在原始 value 里按下标精确删除。
      const index = this.resolveIndex(file);
      if (index < 0) {
        return;
      }
      const newList = (Array.isArray(this.value) ? this.value : []).slice();
      newList.splice(index, 1);
      this.$emit("input", newList);
    },
    handleUploadSuccess(res, file) {
      if (res.code === '0') {
        // 基于原始 value 追加，不能用 fileList——那是带 __idx/uid 的渲染副本，
        // 直接 push 会把内部字段一起存进业务数据
        const newList = (Array.isArray(this.value) ? this.value : []).slice();
        newList.push({ url: res.data, name: file.name });
        this.$emit("input", newList);
      } else {
        this.$message({ type: "error", message: res.msg });
      }
    },
    handleExceed(files, fileList) {
      this.$message.error(`最多只能上传${this.limit}张图片`);
    },
    beforeUpload(file) {
      const currentList = this.fileList;
      console.log(currentList.length, this.limit);
      // 在文件选择后立即检查数量限制
      if (currentList.length >= this.limit) {
        this.$message.error(`最多只能上传${this.limit}张图片`);
        return false;
      }
      return true;
    },
    /* 上传 */
    beforeUp(file) {
      var formData = new FormData();
      // file是上传的文件
      formData.append("file", file.file);
      //调用上传接口xxxxx接口地址
      UploadFile(formData).then((res) => {
        if (res.code === '0') {
          // 同 handleUploadSuccess：基于原始 value 追加，避免把渲染副本的
          // __idx/uid 字段写进业务数据
          const newList = (Array.isArray(this.value) ? this.value : []).slice();
          newList.push({ url: res.data, name: file.file.name });
          this.$emit("input", newList);
        } else {
          this.$message({ type: "error", message: res.msg });
        }
      });
    },
    // 判断是否为视频文件
    isVideoFile(file) {
      const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv'];
      const fileName = file.name || file.url || '';
      return videoExtensions.some(ext => fileName.toLowerCase().includes(ext));
    },
    // 处理预览
    handlePreview(file) {
      if (this.isVideoFile(file)) {
        // 视频预览
        this.isVideoPreview = true;
        this.currentVideoUrl = file.url;
        this.showViewer = true;
      } else {
        // 图片预览
        this.isVideoPreview = false;
        let arr = [];
        arr.push(file.url);
        this.previewList = arr;
        this.showViewer = true;
      }
    }
  },
  watch: {
    value: {
      handler(newVal) {
        // 确保 value 始终是数组
        if (!Array.isArray(newVal)) {
          this.$emit("input", []);
        }
      },
      immediate: true
    }
  },
};
</script>

<style scoped lang="scss">
.image {
  position: relative;
}
.image .mask {
  opacity: 0;
  position: absolute;
  top: 0;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  transition: all 0.3s;
}
.image:hover .mask {
  opacity: 1;
}
.hint {
  font-size: 12px;
  color: #9b9c9d;
  line-height: 16px;
  padding-left: 10px;
}

.upload-item {
  position: relative;
  width: 40px;
  height: 40px;
  margin-right: 10px;
  display: inline-block;
  transition: none !important;
  animation: none !important;
}

.upload-image {
  width: 40px;
  height: 40px;
  object-fit: cover;
  cursor: pointer;
  border-radius: 4px;
}

.video-preview {
  width: 40px;
  height: 40px;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
}

.video-icon {
  font-size: 20px;
  color: #409eff;
}

.delete-icon {
  position: absolute;
  right: -5px;
  top: -5px;
  width: 18px;
  height: 18px;
  background: #fff;
  // border: 1px solid #333;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  color: #333;
  z-index: 10;
}

.video-container {
  text-align: center;
}

::v-deep .el-upload {
  text-align: left;
  display: flex;
}
::v-deep .el-upload-list {
  display: flex;
  flex-wrap: wrap;
  transition: none !important;
  animation: none !important;
  .el-upload-list__item-name {
    // display: none;
  }
  .el-upload-list__item {
    transition: none !important;
    animation: none !important;
  }
}
::v-deep .el-upload-list--picture .el-upload-list__item {
  height: 40px !important;
  width: 40px !important;
  padding: 0 !important;
  margin-right: 10px !important;
  margin-bottom: 10px !important;
  overflow: inherit !important;
  transition: none !important;
  animation: none !important;
  flex-shrink: 0;
  img {
    width: 40px;
    height: 40px;
    margin-left: 0;
    transition: none !important;
    animation: none !important;
  }
  .el-icon-close {
    left: 30px;
    top: -9px;
    z-index: 9999;
    font-size: 16px;
    font-weight: bold;
    color: #333;
    display: block;
    &::before {
      border: 1px solid #333;
      border-radius: 50%;
    }
  }
}
::v-deep .el-icon-document::before {
  display: none;
}
</style>